import { UPLOAD_API } from '../config/uploadApi';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Ghost, ChevronUp } from 'lucide-react';
import { isPersonalAdminRequest } from '../context/personalAdminScope';
import './PersonalAdminSpace.css';
import { useAuth, PersonalUserProvider } from '../context/AuthContext';
import { GhostChangeTrackerProvider, useGhostChangeTracker } from '../context/GhostChangeTrackerContext';
import '../pages/AdminDashboardPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';


export function PersonalAdminSpace({ children }: { children: ReactNode }) {
    const location = useLocation();
    const target = new URLSearchParams(location.search).get('ghostUser');
    if (target === null) return children;
    if (!target) return <p role="alert">No Personal Space user selected.</p>;
    return <PersonalAdminSession key={target} target={target}>{children}</PersonalAdminSession>;
}

function PersonalAdminSession({ target, children }: { target: string; children: ReactNode }) {
    const auth = useAuth();
    const [profile, setProfile] = useState<any>(null);
    const [error, setError] = useState('');
    const refresh = async () => {
        const response = await axios.get(`${API}/users/me`, { headers: { 'x-personal-user': target } });
        setProfile({ ...response.data, isAdmin: false, isSemiAdmin: false });
    };
    useEffect(() => {
        if (!auth.user?.isAdmin) return;
        let alive = true;

        const id = axios.interceptors.request.use(config => {
            if (isPersonalAdminRequest(config.url || '', API, window.location.origin, UPLOAD_API)) {
                config.headers.set('x-personal-user', target);
            }
            return config;
        });
        axios.get(`${API}/users/me`, { headers: { 'x-personal-user': target } })
            .then(response => { if (alive) setProfile({ ...response.data, isAdmin: false, isSemiAdmin: false }); })
            .catch(() => { if (alive) setError('Could not open this Personal Space.'); });
        return () => { alive = false; axios.interceptors.request.eject(id); };
    }, [target, auth.user?.isAdmin]);
    if (!auth.user?.isAdmin) return <p role="alert">Only site administrators can enter another Personal Space.</p>;
    if (error) return <p role="alert">{error}</p>;
    if (!profile) return <p role="status">Opening Personal Space…</p>;
    return <GhostChangeTrackerProvider isAdmin personalUserId={target}>
        <PersonalUserProvider user={profile} refreshUser={refresh}>
            <PersonalReview name={profile.ingameName || profile.faceitName || profile.username}>{children}</PersonalReview>
        </PersonalUserProvider>
    </GhostChangeTrackerProvider>;
}

function PersonalReview({ name, children }: { name: string; children: ReactNode }) {
    const tracker = useGhostChangeTracker();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const floatingButtonRef = useRef<HTMLButtonElement>(null);
    const collapseButtonRef = useRef<HTMLButtonElement>(null);
    const restoreToggleFocus = useRef(false);
    const toggleCollapsed = (value: boolean) => {
        restoreToggleFocus.current = true;
        setCollapsed(value);
    };
    useEffect(() => {
        if (!restoreToggleFocus.current) return;
        (collapsed ? floatingButtonRef : collapseButtonRef).current?.focus();
        restoreToggleFocus.current = false;
    }, [collapsed]);
    const pending = tracker.pending.filter(change => change.applyStatus !== 'applied');
    useEffect(() => {
        const leave = (event: BeforeUnloadEvent) => { if (pending.length) { event.preventDefault(); event.returnValue = ''; } };
        window.addEventListener('beforeunload', leave);
        return () => window.removeEventListener('beforeunload', leave);
    }, [pending.length]);
    return <div className="personal-admin-space">
        {collapsed ? <button
            ref={floatingButtonRef}
            type="button"
            className="admin-ghost-floating-button"
            aria-label={`Expand Ghost Mode, Personal Space: ${name}, ${pending.length} changes to review`}
            aria-expanded={false}
            title={`Show Ghost Mode — ${name}`}
            onClick={() => toggleCollapsed(false)}
        >
            <Ghost size={16} aria-hidden="true" />
            {pending.length > 0 && <span className={`admin-ghost-floating-count${pending.some(change => change.applyStatus === 'error') ? ' has-errors' : ''}`} aria-hidden="true">{pending.length > 99 ? '99+' : pending.length}</span>}
        </button> : <div className="admin-ghost-banner" style={{ position: 'relative', zIndex: 20000 }}>
            <span>Ghost Mode — Personal Space: <strong>{name}</strong></span>
            <button onClick={() => setOpen(!open)}>Review Changes ({pending.length})</button>
            <button disabled={tracker.isApplying} onClick={() => {
                if (pending.length && !window.confirm('Discard unapplied changes and return to Admin?')) return;
                tracker.discardAll(); navigate('/admin/users');
            }}>Back to Admin</button>
            <button ref={collapseButtonRef} type="button" className="admin-ghost-collapse-button"
                aria-label="Collapse Ghost Mode" aria-expanded={true} title="Collapse Ghost Mode"
                onClick={() => toggleCollapsed(true)}><ChevronUp size={17} aria-hidden="true" /></button>
        </div>}
        {!collapsed && open && <div className="ghost-changes-panel" style={{ top: 46, zIndex: 20001 }}>
            <div className="ghost-changes-actions">
                <button disabled={tracker.isApplying || !pending.some(c => c.checked)} onClick={async () => { await tracker.applyChecked(); }}>Apply Selected</button>
                <button disabled={tracker.isApplying} onClick={() => { tracker.discardAll(); window.dispatchEvent(new Event('personal-admin-discard'));  }}>Discard All</button>
            </div>
            {tracker.pending.map(change => <label key={change.id} className="ghost-change-item">
                <input type="checkbox" checked={change.checked} disabled={tracker.isApplying || change.applyStatus === 'applied'} onChange={() => tracker.toggleCheck(change.id)} />
                <span>{change.method} {change.label} — {change.applyStatus}</span>
                {change.errorMessage && <span role="alert">{change.errorMessage}</span>}
            </label>)}
        </div>}
        <div>{children}</div>
    </div>;
}
